import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Modal } from '@/components/Modal';

interface SubtenantConfirmationDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  parentDisplayName: string;
  childDisplayName: string;
}

/**
 * Confirmation dialog shown before creating a subtenant relationship.
 * Warns that all users with access to the parent account will automatically
 * gain access to the child account.
 */
export const SubtenantConfirmationDialog: React.FC<
  SubtenantConfirmationDialogProps
> = ({ isOpen, onConfirm, onCancel, parentDisplayName, childDisplayName }) => {
  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title="Confirm Subtenant Relationship"
      size="lg"
      style="default"
      closeOnOutsideClick={false}
      hideFooter={true}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4 dark:bg-amber-950/30">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">
              Access will be shared automatically
            </p>
            <p className="mt-1 text-amber-700 dark:text-amber-300">
              All users with access to <strong>{parentDisplayName}</strong> will
              automatically gain access to <strong>{childDisplayName}</strong>{' '}
              with their current role.
            </p>
          </div>
        </div>

        <p className="text-chariot-text-secondary text-sm">
          This creates a parent-child relationship where the parent
          account&apos;s collaborators inherit access to the child account. This
          can be removed later.
        </p>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onCancel}
            className="border-chariot-stroke text-chariot-text-secondary hover:bg-chariot-background-secondary rounded border px-4 py-2 text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded px-4 py-2 text-sm text-white transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </Modal>
  );
};
